import { ChangeEvent, useState } from 'react';

import {
  buildUpsertParams,
  checkError,
  DeliveryFeeType,
  DeliveryProvider,
  initializeSites,
  PreferenceType,
  SiteDelivery,
} from '@/pages/camel-order/delivery/service';
import { DeliveryFormError } from '@/pages/camel-order/delivery/validate';
import { routes } from '@/router';
import { useCamelOrderDeliveryList } from '@/services/Delivery/hooks/list/useCamelOrderDeliveryList';
import { useCamelOrderDeliveryUpsert } from '@/services/Delivery/hooks/upsert/useCamelOrderDeliveryUpsert';

type Return = {
  sites: SiteDelivery[];
  isLoading: boolean;
  siteIndex: number;
  error: DeliveryFormError;
  handleChangeSiteUrl: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  handleChangeDeliveryFeeType: (newStatus: React.ReactText) => void;
  handleChangeBaseDeliveryFee: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeBaseDeliveryFeeDistance: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;
  handleChangeAdditionalDistanceFee: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeAdditionalDistance: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangeFixedDiscountAmount: (e: ChangeEvent<HTMLInputElement>) => void;
  handleChangePreferenceType: (newValue: React.ReactText) => void;
  handleChangeDeliveryProviderChecked: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;
  handleChangeUberDirectCustomerId: (e: ChangeEvent<HTMLInputElement>) => void;
  initialize: () => Promise<void>;
  handleUpdate: () => Promise<void>;
};

export const useCamelOrderDeliveryForm = (): Return => {
  const [sites, setSites] = useState<SiteDelivery[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [siteIndex, setSiteIndex] = useState<number>(0);
  const errorObj = { message: '', isError: false, isTouched: false };
  const initError = {
    baseDeliveryFee: { ...errorObj },
    baseDeliveryFeeDistance: { ...errorObj },
    additionalDistanceFee: { ...errorObj },
    additionalDistance: { ...errorObj },
    fixedDiscountAmount: { ...errorObj },
    uberDirectCustomerId: { ...errorObj },
  };
  const [error, setError] = useState<DeliveryFormError>(initError);

  const handleChangeSiteUrl = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = Number(e.target.value);
    setSiteIndex(index);
    setError(initError);
  };

  const handleChangeDeliveryFeeType = (newStatus: React.ReactText) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.delivery_fee_type =
      newStatus as DeliveryFeeType;
    setSites(copySite);
  };

  const handleChangeBaseDeliveryFee = (e: ChangeEvent<HTMLInputElement>) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.base_delivery_fee = e.target.value;
    setSites(copySite);
  };

  const handleChangeBaseDeliveryFeeDistance = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.base_delivery_fee_distance = e.target.value;
    setSites(copySite);
  };

  const handleChangeAdditionalDistanceFee = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.additional_distance_fee = e.target.value;
    setSites(copySite);
  };

  const handleChangeAdditionalDistance = (e: ChangeEvent<HTMLInputElement>) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.additional_distance = e.target.value;
    setSites(copySite);
  };

  const handleChangeFixedDiscountAmount = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.fixed_discount_amount = e.target.value;
    setSites(copySite);
  };

  const handleChangePreferenceType = (newValue: React.ReactText) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.preference_type = newValue as PreferenceType;
    setSites(copySite);
  };

  const handleChangeDeliveryProviderChecked = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const copySite = [...sites];
    if (e.target.checked) {
      copySite[siteIndex].delivery.delivery_providers.push(
        e.target.value as DeliveryProvider
      );
    } else {
      copySite[siteIndex].delivery.delivery_providers = copySite[
        siteIndex
      ].delivery.delivery_providers.filter((value) => value !== e.target.value);
    }
    setSites(copySite);
  };

  const handleChangeUberDirectCustomerId = (
    e: ChangeEvent<HTMLInputElement>
  ) => {
    const copySite = [...sites];
    copySite[siteIndex].delivery.uber_direct_customer_id = e.target.value;
    setSites(copySite);
  };

  const handleUpdate = async () => {
    const copySite = [...sites];
    const targetSite = copySite[siteIndex];
    const newError = checkError(targetSite, error);
    const isError = Object.values(newError).some((error) => error.isError);
    if (isError) {
      setError({ ...newError });
      return alert('入力内容をご確認ください。');
    }
    const params = buildUpsertParams(targetSite);
    await useCamelOrderDeliveryUpsert(params);
    alert(`${routes.camelOrderDelivery.title}を更新しました。`);
    setError(initError);
  };

  const initialize = async () => {
    setIsLoading(true);
    const res = await useCamelOrderDeliveryList();
    const buildSites = initializeSites(res);
    setSites(buildSites);
    setIsLoading(false);
  };

  return {
    sites,
    isLoading,
    siteIndex,
    error,
    handleChangeSiteUrl,
    handleChangeDeliveryFeeType,
    handleChangeBaseDeliveryFee,
    handleChangeBaseDeliveryFeeDistance,
    handleChangeAdditionalDistanceFee,
    handleChangeAdditionalDistance,
    handleChangeFixedDiscountAmount,
    handleChangePreferenceType,
    handleChangeDeliveryProviderChecked,
    handleChangeUberDirectCustomerId,
    handleUpdate,
    initialize,
  };
};
